// frontend/src/modules/monitoring/components/AgentSetupCard.tsx
//
// The employee side of monitoring distribution. When the owner toggles an
// employee into monitoring (agent_required), web clock-in is refused — so the
// EMPLOYEE's dashboard must hand them everything needed to get the desktop
// app running: installers, a one-click setup deep link (auto-fills token +
// server in the app), and a token to copy as fallback. Renders nothing for
// non-monitored users, and a slim "connected" confirmation once the agent has
// heartbeated. Self-contained: fetches its own state, silent on any failure
// (a broken card must not break the dashboard).

import * as React from 'react';
import { toast } from 'sonner';
import { MonitorDown, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { VISUAL } from '../../../design-system/visual';
import { monitoringApi, type SelfAgentSetup } from '../api';
import { agentReleasesApi, type AgentRelease } from '../../agent-releases/api';
import { ApiError } from '../../../core/api-client';
import { buildDeepLink, navigateToDeepLink } from '../deepLink';

export function AgentSetupCard() {
  const [setup, setSetup] = React.useState<SelfAgentSetup | null>(null);
  const [release, setRelease] = React.useState<AgentRelease | null>(null);
  const [plaintext, setPlaintext] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [checking, setChecking] = React.useState(false);

  const load = React.useCallback(() => {
    monitoringApi.getAgentSetup().then(setSetup).catch((err) => {
      // 403 module_disabled etc. — the card simply doesn't apply.
      if (!(err instanceof ApiError)) console.warn('[agent-setup] fetch failed', err);
      setSetup(null);
    });
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (setup?.monitored && !setup.connected) {
      agentReleasesApi.getTenant().then(setRelease).catch(() => undefined);
    }
  }, [setup?.monitored, setup?.connected]);

  async function getSetupLink() {
    setBusy(true);
    try {
      const r = await monitoringApi.issueSelfToken();
      setPlaintext(r.plaintext);
      // Fire the deep link immediately — the button row + manual fallback
      // stay rendered below so nothing is lost if the browser blocks it.
      navigateToDeepLink(buildDeepLink(r.plaintext));
    } catch (err: any) {
      toast.error('Could not prepare your setup link', { description: err?.message });
    } finally { setBusy(false); }
  }

  async function checkConnection() {
    setChecking(true);
    try {
      const s = await monitoringApi.getAgentSetup();
      setSetup(s);
      if (s.connected) toast.success('Desktop app connected');
      else toast.info('Not connected yet', { description: 'Finish setup in the app — it checks in within ~20 seconds.' });
    } catch { /* keep prior state */ }
    finally { setChecking(false); }
  }

  async function copyToken() {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      toast.success('Token copied');
    } catch { toast.error('Could not copy — select and copy manually'); }
  }

  if (!setup?.monitored) return null;

  if (setup.connected) {
    return (
      <section className={[VISUAL.bento, 'p-4 flex items-center gap-3'].join(' ')}>
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-foreground">Worqly desktop app connected</p>
          <p className="text-[12.5px] text-muted-foreground">
            Clock in and out from the app{setup.last_seen_version ? ` · version ${setup.last_seen_version}` : ''}.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={[VISUAL.bento, 'p-5 sm:p-6'].join(' ')}>
      <div className="flex items-start gap-4">
        <div className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 shrink-0">
          <MonitorDown className="h-5 w-5 text-saffron-700" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-bold text-foreground">Install the Worqly desktop app to clock in</h3>
          <p className="text-[13px] text-muted-foreground mt-1">
            Your workspace tracks work sessions through the desktop app, so clocking in from the
            browser is turned off for you. Install the app once — after that it handles clock in,
            clock out and breaks.
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            {release?.windows_url && (
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <a href={release.windows_url}>Download for Windows</a>
              </Button>
            )}
            {release?.macos_url && (
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <a href={release.macos_url}>Download for macOS</a>
              </Button>
            )}
            {!plaintext && (
              <Button size="sm" className="rounded-full" onClick={getSetupLink} disabled={busy}>
                {busy ? 'Preparing…' : 'Get my setup link'}
              </Button>
            )}
          </div>

          {plaintext && (
            <div className="mt-4 grid gap-2 border-t border-border/40 pt-4">
              <p className="text-[12.5px] text-muted-foreground">
                <strong className="text-foreground">We just tried to open the Worqly app for you.</strong> If
                it didn't open, click below — or download it first if it isn't installed yet.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild size="sm" className="rounded-full">
                  <a href={buildDeepLink(plaintext)}>Open the app &amp; finish setup</a>
                </Button>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={checkConnection} disabled={checking}>
                  <RefreshCw className={checking ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
                  Check connection
                </Button>
              </div>
              <details className="mt-1">
                <summary className="text-[12px] text-muted-foreground cursor-pointer">Manual setup (paste the token in the app)</summary>
                <div className="flex items-center gap-2 mt-2">
                  <Input readOnly value={plaintext} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs h-8" />
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={copyToken}>Copy</Button>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
